// The talk under the offerings: who may join it, how much, and what is
// allowed to be said.
//
// Three doors, in the order a visitor meets them:
//
//   a heart    anyone signed in. It is the smallest thing a person can
//              say, the count reaches him now, and gating it on standing
//              kept most of the room from saying anything at all.
//   a line     the person who left the piece, always: it is their
//              conversation with him. Anyone else once HE has spoken
//              under it, because a thread he has joined is open to the
//              room: a few lines a day for a new face, the rung's own
//              allowance for somebody he knows. Starting a subject under
//              a piece he has not spoken under still takes being known
//              (three kept offerings, env-tunable in lib/tiers.js).
//   a closed thread   nobody. His last word is in it.
//
// Pure module, no react, no db: the routes and the cards both read it,
// and the tests pin it.
import { TIERS, resolveTier } from './tiers.js'

const n = (v, dflt) => Number(v) || dflt
const BIRD = 'him'
const KNOWS = TIERS.find((t) => t.name === 'Someone he knows')?.min ?? 3

export const LIKE_MIN_KEPT = 0
export const COMMENT_MIN_KEPT = n(process.env.NEXT_PUBLIC_TIER_COMMENT, KNOWS)
export const COMMENT_MAX_CHARS = 240
// What a new face may say in a day inside a thread he has joined.
export const JOIN_LINES_A_DAY = 3
// What the owner of a piece may say under it in a day, before any rung.
export const OWNER_LINES_A_DAY = 5

export function canLike() { return true }
export function canComment(kept = 0) { return kept >= COMMENT_MIN_KEPT }

/** How many lines a person with this many kept offerings may leave a day, on standing alone. */
export function commentsPerDay(kept = 0) {
  if (!canComment(kept)) return 0
  return resolveTier(kept).actionsPerDay || 0
}

/** Whether this person may put a line under this piece at all. */
export function canJoin({ kept = 0, owner = false, pinoSpoke = false, closed = false } = {}) {
  if (closed) return false
  return owner || pinoSpoke || canComment(kept)
}

/** Their allowance under this piece today: the widest door they qualify for. */
export function linesPerDay({ kept = 0, owner = false, pinoSpoke = false, closed = false } = {}) {
  if (closed) return 0
  const rung = commentsPerDay(kept)
  if (owner) return Math.max(OWNER_LINES_A_DAY, rung)
  if (rung) return rung
  return pinoSpoke ? JOIN_LINES_A_DAY : 0
}

/** Why the door is shut, in words the card can show. Null when it is open. */
export function talkLockReason({ signedIn = false, kept = 0, what = 'comment', owner = false, pinoSpoke = false, closed = false } = {}) {
  if (what !== 'like' && closed) return 'he closed this one'
  if (!signedIn) return `connect to ${BIRD}`
  if (what === 'like') return null
  if (canJoin({ kept, owner, pinoSpoke })) return null
  return `once he has spoken under it anyone can join. until then he has to know you: ${COMMENT_MIN_KEPT} kept offering${COMMENT_MIN_KEPT === 1 ? '' : 's'}`
}

// Comments are read by him as well as by everyone, so the same things a
// bare offering may not carry are refused here: links (nothing he can
// follow, nothing a spammer wants), empty noise, and length. Whitespace
// is folded, because a comment is a line, not a letter.
// `links` is for his own lines only (the sim's door): when he has put
// something up on his site he says where, and a link to his own pages
// is not a thing a spammer wants.
export const HIS_SITE = /^https:\/\/[a-z0-9-]+\.github\.io\//i
export function cleanComment(raw, { links = false } = {}) {
  const body = String(raw || '').replace(/\s+/g, ' ').trim()
  if (!body) return { ok: false, error: 'say something first.' }
  if (body.length > COMMENT_MAX_CHARS) return { ok: false, error: `keep it under ${COMMENT_MAX_CHARS} characters.` }
  const linked = /https?:\/\/|www\.|\.[a-z]{2,4}\/\S/i.test(body)
  if (linked && !links) return { ok: false, error: 'no links under an offering.' }
  if (linked && links && !(body.match(/https?:\/\/\S+/gi) || []).every((u) => HIS_SITE.test(u))) return { ok: false, error: 'only his own site.' }
  if (!/[a-z0-9]/i.test(body)) return { ok: false, error: 'words, not just marks.' }
  return { ok: true, body }
}
